import { AuthRouteContext, type CanRedirectToUrl } from '@cosuno/authentication-frontend';
import React, { type PropsWithChildren, useMemo } from 'react';
import { matchPath, Switch as ReactRouterSwitch } from 'react-router-dom';

import type { AuthRouteProps } from './AuthRoute';

interface AuthRoute {
  props: AuthRouteProps;
}

function isAuthRoute(child: unknown): child is AuthRoute {
  return Boolean(
    typeof child === 'object' &&
      child &&
      'props' in child &&
      typeof child.props === 'object' &&
      child.props &&
      'path' in child.props &&
      typeof child.props.path === 'string',
  );
}

export const Switch: React.FC<PropsWithChildren> = ({ children }) => {
  const canRedirectToUrl = useMemo<CanRedirectToUrl>(() => {
    const authRoutes = React.Children.toArray(children)
      .filter(isAuthRoute)
      .map((child) => {
        const { props } = child as unknown as AuthRoute;
        return props as unknown as AuthRoute['props'];
      });

    return ({ redirect, isUserAnonymous, isUserPrincipal, isUserAgent }) => {
      const pathnameWithoutQuery = (redirect.startsWith('/') ? redirect : `/${redirect}`).split(
        '?',
      )[0];
      const authRoute = authRoutes.find((props) => Boolean(matchPath(pathnameWithoutQuery, props)));
      if (isUserAnonymous) {
        return Boolean(authRoute?.allowAnonymous);
      }
      if (isUserPrincipal) {
        return Boolean(authRoute?.allowPrincipal);
      }
      if (isUserAgent) {
        return Boolean(authRoute?.allowAgent);
      }
      return true;
    };
  }, [children]);

  return (
    <AuthRouteContext.Provider value={{ canRedirectToUrl }}>
      <ReactRouterSwitch>{children}</ReactRouterSwitch>
    </AuthRouteContext.Provider>
  );
};
